import { APP_TIMINGS_GROUP, getPerformance, trackTimingLane } from '@/perf/performanceApi'
import { globalRef, type Timestamp } from '@amazon/vinyl-util'
import type { LoadSpanKind } from '@amazon/vinyl'
import { createPlaybackDelayMeasure } from '@amzn/bf-playback'

export type PlaybackMeasureToken = number

const measureRef = globalRef(() =>
    createPlaybackDelayMeasure(
        () => getPerformance().now(),
        ({ start, end, currentTrackTitle }) =>
            getPerformance().timeStamp({
                label: 'playback delay',
                start,
                end,
                trackName: trackTimingLane(currentTrackTitle ?? ''),
                trackGroup: APP_TIMINGS_GROUP,
                color: 'primary',
            })
    )
)

/**
 * A user expressed intent to start a NEW track. Opens the journey window ahead of
 * createQueue so the whole interaction→playing span is captured, and returns the
 * owner token so a caller's fail seam only clears its own window. A newer
 * interaction overwrites (wins).
 *
 * Must fire only for a new-track start — never a pause/unpause/resume of the
 * current track, never a machine advance (autoplay, ended→skipNext, dislike).
 */
export function markInteractionStart(ageSeconds?: number): PlaybackMeasureToken {
    return measureRef.value.markInteractionStart(ageSeconds)
}

/**
 * Drop the pending window without drawing (a createQueue rejection). A stale
 * token — a newer interaction already owns the window — is a no-op.
 */
export function clearInteractionStart(token?: PlaybackMeasureToken): void {
    measureRef.value.clearInteractionStart(token)
}

/**
 * A play reached the queue sink (setQueue). Opens the window if no
 * markInteractionStart did — covers paths that bypass the interaction seam
 * (detail heroes, track rows) — keeping an earlier interaction's window. Returns
 * the owning token.
 */
export function markPlayStart(ageSeconds?: number): PlaybackMeasureToken {
    return measureRef.value.markPlayStart(ageSeconds)
}

/** The token owning the current window, captured by a sub-player when its entry
 * becomes current so its `playing` / terminal-error seams act on the window it
 * owns, not a newer play's. */
export function currentMeasureToken(): PlaybackMeasureToken {
    return measureRef.value.currentMeasureToken()
}

/**
 * The engine emitted `playing`. Closes the window with the interaction→playing
 * bar drawn in the now-playing track's lane. A pure resume (no open window) draws
 * nothing; a stale-token or over-age window is ignored.
 */
export function markPlaying(currentTrackTitle?: string, token?: PlaybackMeasureToken): void {
    measureRef.value.markPlaying(currentTrackTitle, token)
}

/**
 * The interaction→playing span (ms) a play measured at its `playing` — the FULL
 * initial-playback delay (the user gesture through the engine's `playing`), for
 * the streaming metrics layer. This already sums the app's interaction→play()
 * portion and the engine's play()→playing portion, so it maps to GH's total
 * `initialPlaybackDelayMilliseconds` directly. Null when the play drew no window
 * (a pure resume, or a programmatic play with no interaction) — the caller then
 * falls back to the engine-only delay, which is what GH emits in that case.
 */
export function peekPlaybackDelayMs(token?: PlaybackMeasureToken): number | null {
    return measureRef.value.peekPlaybackDelayMs(token)
}

/** When the press that opened the play's measured window happened (epoch ms); null when it drew none. */
export function peekPlaybackPressAt(token?: PlaybackMeasureToken): number | null {
    return measureRef.value.peekPlaybackPressAt(token)
}

/**
 * Abandon the pending window when a play fails before audio starts (rejected
 * step/play, empty queue, terminal engine error). Guarded on the failing play's
 * token so a superseded play's failure can't wipe the incoming play's window.
 */
export function cancelPlaybackMeasure(reason?: string, token?: PlaybackMeasureToken): void {
    measureRef.value.cancelPlaybackMeasure(reason, token)
}

export function interactionAgeSeconds(): number | null {
    return measureRef.value.interactionAgeSeconds()
}

/**
 * App-issued load phases that run outside vinyl's engine — the Firefly track
 * lookup and the PRS token/resources fetches that precede vinyl's own manifest
 * (corrected.mpd) fetch. Drawn on the same track load lane as vinyl's
 * {@link LoadSpanKind} spans so the whole IPD chain reads on one timeline. The
 * Firefly lookup is a gql query, so it carries the `gqlFetch_` prefix every
 * other gql call's span gets from `instrument` in `service/gql.ts` — e.g.
 * createQueue's span, which lands in this same track lane via `timingTrack`.
 */
export type AppLoadSpanKind =
    | LoadSpanKind
    | 'gqlFetch_videoTrackPlayback'
    | 'playbackToken'
    | 'GetPlaybackResources'

// Coarse hand-tuned "nominal" duration per load stage, in ms — the healthy
// round-trip a measured span is color-coded against. Adjust as real p50 lands.
const NOMINAL_DURATION_MS: Record<AppLoadSpanKind, number> = {
    gqlFetch_videoTrackPlayback: 150,
    playbackToken: 150,
    GetPlaybackResources: 250,
    manifest: 200,
    keySetup: 80,
    license: 200,
    initSegment: 100,
    firstSegment: 250,
}

// Deviation bands keyed on measured / nominal (green → yellow → red). DevTools'
// color arg is a fixed palette enum (no hex), so this is a discrete scale.
const DEVIATION_COLOR_BANDS: readonly { readonly maxRatio: number; readonly color: string }[] = [
    { maxRatio: 1.5, color: 'tertiary' },
    { maxRatio: 3, color: 'warning' },
]
const OVER_BUDGET_COLOR = 'error'

/** Color a load span by how far its measured duration deviates from the nominal
 * for its kind — green (close), yellow (slower), red (much slower). */
function loadSpanColor(kind: AppLoadSpanKind, durationMs: number): string {
    const ratio = durationMs / NOMINAL_DURATION_MS[kind]
    return DEVIATION_COLOR_BANDS.find((band) => ratio <= band.maxRatio)?.color ?? OVER_BUDGET_COLOR
}

// A content-bearing span (license, init/first segment) carries the content it
// was measured for — the mimeType when known, else the coarser contentType —
// rendered as a compact `_audio`/`_video` suffix. A kind with no content type
// (a bare `license` certificate exchange, `manifest`) labels by its bare kind.
function loadSpanLabel(span: {
    readonly kind: AppLoadSpanKind
    readonly mimeType?: string | undefined
    readonly contentType?: string | undefined
}): string {
    const mediaType = (span.mimeType ?? span.contentType)?.split('/')[0]
    return mediaType === 'audio' || mediaType === 'video' ? `${span.kind}_${mediaType}` : span.kind
}

/**
 * Draw one vinyl load-stage span (manifest/license/init/first segment) as a bar
 * in the track's load lane. `startTime`/`endTime` are unix-ms; `track` names the lane.
 */
export function markLoadSpan(span: {
    readonly kind: AppLoadSpanKind
    readonly startTime: Timestamp
    readonly endTime: Timestamp
    readonly track: string
    readonly mimeType?: string | undefined
    readonly contentType?: string | undefined
}): void {
    // Clamp against a backward wall-clock step (NTP) so the bar never reverses.
    const end = Math.max(span.startTime, span.endTime)
    getPerformance().timeStamp({
        label: loadSpanLabel(span),
        start: span.startTime,
        end,
        trackName: trackTimingLane(span.track),
        trackGroup: APP_TIMINGS_GROUP,
        color: loadSpanColor(span.kind, end - span.startTime),
    })
}

/**
 * Time an async load-stage fetch and draw its span in the track's lane once it
 * resolves — the VOD analogue of the `loadSpan` events vinyl's engine emits for
 * audio, for the PRS/DRM fetches that run in app code outside the engine. A
 * rejected fetch draws nothing (the stage never completed) and rethrows.
 */
export async function measureLoadSpan<T>(
    kind: AppLoadSpanKind,
    track: string,
    fetch: () => Promise<T>
): Promise<T> {
    const perf = getPerformance()
    const startTime = perf.now()
    const result = await fetch()
    markLoadSpan({ kind, startTime, endTime: perf.now(), track })
    return result
}
